import { useTranslation } from 'react-i18next'
import type { PlanificacionSemanalRow } from '@/types'
import { fmt, pct } from '@/utils/planningFormatters'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { BotonMarca, CabeceraMarca, useMarcadas, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { asEstadoMaterial, tiempoDeFecha } from '@/utils/estadoMaterial'
import { COMP_BADGE, COMP_ROW } from '@/components/production-sequence/sequenceFormat'

export function ConsumoSubTable({ items, dark, onSelect, resalta, clavePadre }: {
  items: PlanificacionSemanalRow[]
  dark: boolean
  onSelect: (item: PlanificacionSemanalRow) => void
  /** Componentes que casan con la busqueda. Van resaltados para que se vea POR
   *  QUE ha salido esta produccion: sin esto el usuario busca una materia prima
   *  y se encuentra una OP cuyo codigo y articulo no se le parecen en nada. */
  resalta?: ((item: PlanificacionSemanalRow) => boolean) | null
  /** Clave de marcado de la OP que abre esta subtabla. Las claves de los
   *  componentes se cuelgan de ella, de modo que el mismo material consumido por
   *  dos producciones son dos marcas distintas. */
  clavePadre: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  // Mismo ambito que la fila de OP: la franja de recuento del Gantt cuenta las
  // OPs y sus componentes juntos, que es lo que el usuario ha ido marcando.
  const marcas = useMarcasFilas('gantt')
  // `useMarcadas` y no `FilaMarcable`: las filas se pintan dentro de un `.map`,
  // donde no cabe un hook por fila. La subtabla es corta (los componentes de una
  // sola OP), asi que repintarla entera al marcar no cuesta nada.
  const marcadas = useMarcadas(marcas)
  const marcadoCls = dark ? 'fila-marcada-dark' : 'fila-marcada'

  if (!items.length) return null
  const bg     = dark ? 'bg-slate-950/60' : 'bg-slate-50/80'
  const border = dark ? 'border-white/5'  : 'border-slate-200/60'
  const thCls  = dark ? 'text-slate-500'  : 'text-slate-400'

  const HEADERS = [
    // El sangrado que antes daba `pl-8` lo aporta ya el canalon de marcado.
    { key: 'component',   label: t('planning.consumo.component'),            align: 'left' },
    { key: 'estimated',   label: t('planning.consumo.estimatedConsumption'),  align: 'right' },
    { key: 'reservation', label: t('planning.consumo.reservation'),           align: 'right' },
    { key: 'pending',     label: t('planning.consumo.pending'),               align: 'right' },
    { key: 'realAvail',   label: t('planning.consumo.realAvail'),             align: 'right' },
    { key: 'availBefore', label: t('planning.consumo.availBefore'),           align: 'right' },
    { key: 'availAfter',  label: t('planning.consumo.availAfter'),            align: 'right' },
    { key: 'nextEntry',   label: t('planning.consumo.nextEntry'),             align: 'left' },
    { key: 'coverage',    label: t('planning.consumo.coverage'),              align: 'right' },
    { key: 'action',      label: '',                                           align: 'center w-14' },
  ]

  return (
    <div className={`${bg} overflow-x-auto border-t ${border}`} style={{ minWidth: 0 }}>
      <table className="w-full text-[11px]" style={{ minWidth: 1064 }}>
        <thead>
          <tr className={`border-b ${border}`}>
            <CabeceraMarca />
            {HEADERS.map(h => (
              <th key={h.key} className={`px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider ${thCls} text-${h.align.includes('right') ? 'right' : h.align.includes('center') ? 'center' : 'left'} ${h.align.includes('pl-8') ? 'pl-8' : ''} ${h.align.includes('w-14') ? 'w-14' : ''}`}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const cob     = item.consumoEstimado > 0 ? pct(item.consumoEstimado - Math.max(0, item.pendiente), item.consumoEstimado) : 100
            // El estado se lee aqui arriba porque lo usan DOS cosas de la misma
            // fila — el fondo y el badge — y tienen que salir del mismo sitio.
            const est     = asEstadoMaterial(item.estadoMaterial)
            const dispNeg = item.disponibleDespues < 0
            const cobClr  = cob >= 100 ? 'bg-emerald-400' : cob >= 70 ? 'bg-yellow-400' : 'bg-red-400'
            const cobTxt  = cob >= 100
              ? (dark ? 'text-emerald-400' : 'text-emerald-600')
              : cob >= 70
                ? (dark ? 'text-yellow-400' : 'text-yellow-600')
                : (dark ? 'text-red-400' : 'text-red-500')
            const casa     = !!resalta?.(item)
            // El fondo lo pone el SEMAFORO del componente, no `flagRotura`.
            //
            // Esta subtabla es donde se contesta la pregunta "¿cual de los diez
            // me esta frenando?", y el binario no sabe contestarla: se enciende
            // igual con una rotura que con una compra en camino, asi que pintaba
            // del mismo rojo palido dos casos que se arreglan de formas
            // distintas — uno es llamar al proveedor y el otro es esperar.
            //
            // OK no tiñe: si se pintara, la fila que hay que mirar dejaria de
            // destacar entre las nueve que estan bien.
            const tinte    = est ? (dark ? COMP_ROW[est].oscuro : COMP_ROW[est].claro) : ''
            // El resaltado de busqueda manda sobre el tinte del estado: es lo que
            // el usuario esta buscando con los ojos en ese momento.
            const rowBg    = casa
              ? (dark ? 'bg-sky-500/12' : 'bg-sky-50')
              : tinte || (dark ? 'hover:bg-white/3' : 'hover:bg-white/80')
            const cellTxt  = dark ? 'text-slate-300' : 'text-slate-700'
            const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
            // El nombre del material, al tono de las cifras: ver el comentario de
            // su celda. Se nombra aparte de `cellTxt` para que se lea en el codigo
            // que es una decision de legibilidad y no una coincidencia.
            const descTxt  = cellTxt
            const feIso    = item.fechaProximaEntradaCubre
            const feDM     = feIso ? `${feIso.slice(8, 10)}/${feIso.slice(5, 7)}` : ''
            // La clave sale del material y sus dimensiones, nunca del indice: la
            // subtabla se repinta con otro filtro y la fila `i` ya no es la misma.
            const clave    = claveFila(clavePadre, item.itemid, item.inventcolorid, item.inventstyleid, item.inventlocationid)
            const marcada  = marcadas.has(clave)
            return (
              <tr
                key={`${item.itemid}-${i}`}
                onClick={() => onSelect(item)}
                // El resaltado de busqueda (el aro sky) y la marca del usuario
                // (el tinte fucsia) son cosas distintas y se ven a la vez: el
                // tinte gana el fondo, el aro sigue rodeando la fila.
                className={`border-b last:border-0 ${border} ${rowBg} ${marcada ? marcadoCls : ''} cursor-pointer ${
                  casa ? (dark ? 'ring-1 ring-inset ring-sky-500/40' : 'ring-1 ring-inset ring-sky-300') : ''
                }`}
              >
                <td className="w-6 px-1 py-1.5 align-middle">
                  <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
                </td>
                <td className="pl-2 pr-3 py-1.5">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`font-semibold ${cellTxt}`}>{fmtArt(item.itemid)}</span>
                    {[item.inventcolorid, item.inventstyleid].filter(Boolean).map((dim, di) => (
                      <span key={di} className={`text-[10px] font-medium px-1 py-px rounded ${dark ? 'bg-white/8 text-slate-400' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                        {dim}
                      </span>
                    ))}
                  </div>
                  {/* Misma correccion que en la fila de OP: en `mutedTxt` esto
                      daba 2,6:1 de contraste y era el nombre del componente, que
                      es justo lo que se lee para saber que material falta. Sube
                      al tono de las cifras de la tabla, un escalon por debajo del
                      codigo en negrita que lleva encima. */}
                  {item.nombreMaterial && (
                    <div className={`text-[10px] ${descTxt} truncate max-w-[200px] mt-0.5`}>{item.nombreMaterial}</div>
                  )}
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${cellTxt}`}>{fmt(item.consumoEstimado, 2)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmt(item.reserva, 2)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${item.pendiente > 0 ? (dark ? 'text-red-400' : 'text-red-500') : mutedTxt}`}>
                  {item.pendiente > 0 ? `+${fmt(item.pendiente, 2)}` : fmt(item.pendiente, 2)}
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${cellTxt}`}>{fmt(item.stockInicial, 2)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${cellTxt}`}>{fmt(item.disponibleAntes, 2)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${dispNeg ? (dark ? 'text-red-400' : 'text-red-500') : (dark ? 'text-emerald-400' : 'text-emerald-600')}`}>
                  {fmt(item.disponibleDespues, 2)}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {feIso ? (
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}
                      title={t('planning.consumo.nextEntryTip', { qty: fmt(item.qtyProximaEntradaCubre ?? 0, 2), date: feDM })}
                    >
                      <span aria-hidden>📦</span>{fmt(item.qtyProximaEntradaCubre ?? 0, 2)} · {feDM}
                    </span>
                  ) : (
                    <span className={item.pendiente > 0 ? (dark ? 'text-red-400' : 'text-red-500') : mutedTxt}>—</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                      <div className={`h-full ${cobClr} rounded-full`} style={{ width: `${cob}%` }} />
                    </div>
                    <span className={`text-[10px] font-bold tabular-nums w-7 text-right shrink-0 ${cobTxt}`}>{cob}%</span>
                  </div>
                </td>
                <td className="px-3 py-1.5 text-center">
                  {/* Cuatro estados donde antes había un binario rojo/verde.
                      El binario decía «hay rotura o no la hay» y con eso un
                      componente que no estaba en el almacén pero venía de camino
                      se pintaba igual que uno que estaba en la estantería.

                      El estado lo calcula el notebook; aquí sólo se pinta. Si
                      llega un valor que no conocemos, `asEstadoMaterial` lo
                      descarta y la celda se queda vacía en vez de estrenar un
                      color sin significado. */}
                  {(() => {
                    if (!est) return <span className={mutedTxt}>—</span>
                    const fe = item.fechaEstado?.slice(0, 10)
                    // «Llegó» y «llegará» no son lo mismo, y en esta columna
                    // conviven: sin decir cuál es, alguien planifica contra una
                    // fecha que todavía no ha ocurrido.
                    // El tiempo verbal no sale sólo del estado: una ROTURA
                    // puede traer la fecha de una compra VENCIDA, y llamarla
                    // «prevista» sería prometer un futuro sobre una fecha que ya
                    // pasó. Decir que venció hace 58 días es lo que convierte el
                    // rojo en una llamada al proveedor en vez de en un muro.
                    const cuando = fe
                      ? t(`planning.consumo.${tiempoDeFecha(est, fe)}`,
                          { fecha: fe.split('-').reverse().join('/') })
                      : t(est === 'ROTURA_STOCK'
                          ? 'planning.consumo.sinFecha'
                          : 'planning.consumo.sinFechaFabricado')
                    const donde = est === 'ENTRE_ALMACENES' && item.almacenesOtrosSpain
                      ? ` · ${t('planning.consumo.enOtrosAlmacenes', {
                          qty: fmt(item.stockOtrosSpain ?? 0, 2),
                          almacenes: item.almacenesOtrosSpain,
                        })}`
                      : ''
                    return (
                      <span
                        className={`text-[9px] font-bold px-1 py-px rounded ${COMP_BADGE[est]}`}
                        title={`${t(`sequence.componentStatus.${est}`)} · ${cuando}${donde}`}
                      >
                        {t(`planning.consumo.estadoCorto.${est}`)}
                      </span>
                    )
                  })()}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
